/**
 * About Page - Company Story & Team
 *
 * Sections:
 * 1. Hero with founder photo
 * 2. Founder's story
 * 3. Mission & Vision
 * 4. Core Values
 * 5. CTA Section
 */

import FixedVideoBackground from "@/components/homepage/FixedVideoBackground";

const AboutPage = () => {
  return (
    <>
      {/* Fixed video background */}
      <FixedVideoBackground />

      <div className="about-page relative min-h-screen py-16 space-y-16">
        {/* FOUNDER SECTION - Photo + Story Side by Side */}
        <div className="max-w-[1600px] mx-auto px-4 sm:px-12">
          <section className="relative bg-white py-16 sm:py-24 lg:py-32 overflow-hidden shadow-2xl rounded-3xl">
            <div className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-6">
              <div className="flex flex-col md:grid md:grid-cols-2 gap-8 lg:gap-12 md:items-start">
                {/* LEFT: Founder's Story */}
                <div className="contents md:block md:pl-6 lg:pl-10">
                  <div className="order-1 md:!order-none">
                    <p className="text-sm uppercase tracking-widest text-slate-500 font-semibold mb-4">
                      Founder's Story
                    </p>
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 leading-tight mb-4 sm:mb-6">
                      Meet Noah Riley
                    </h1>
                  </div>

                  <div className="order-3 md:!order-none space-y-6 text-slate-700 font-semibold leading-relaxed">
                    <p>
                      Noah founded AI Genius Automations with a clear
                      conviction: AI is not some mysterious force in the
                      distance—it's a tool, and like any tool, it's only as good
                      as the people and systems behind it. His approach has
                      always been rooted in curiosity rather than fear. "I don't
                      like the question 'why should I use technology?' That's
                      resistance. 'How can I use the technology?'—that's what I
                      want to move towards." This philosophy drives everything
                      at AI Genius: meeting businesses where they are and
                      showing them how to bring AI into their operations to
                      empower their people and processes.
                    </p>
                    <p>
                      What sets Noah apart is his understanding that AI
                      implementation is about far more than the technology
                      itself. "AI is just a part of a larger system," he says.
                      "It's a tool that we put into a system to make it run more
                      efficiently." He sees AI as a force multiplier—"the
                      exponent of you"—and emphasizes that the people getting
                      the most ROI from AI are thinking of it as a power suit,
                      not a bot. This systems-level thinking, combined with a
                      deep respect for the human element, is what allows AI
                      Genius Automations to deliver solutions that actually
                      transform operations rather than just adding complexity.
                    </p>
                    <p>
                      Noah doesn't shy away from the bigger picture. "This is
                      existential," he acknowledges. "We're really having the
                      questions about why we do what we do and what makes us
                      valuable as human beings." But that's exactly the point.
                      "You're going to die one day and the time that you spend
                      doing things you don't want to do adds up... why not let
                      it do robot stuff so we can get back to doing human
                      stuff—the things that we love." That conviction—freeing
                      people to be people—is what defines AI Genius Automations.
                    </p>
                  </div>

                  {/* Professional Credentials */}
                  <div className="order-4 md:!order-none mt-8 grid grid-cols-3 gap-4">
                    <div className="bg-slate-50 rounded-xl p-4 sm:p-6 text-center">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg mx-auto mb-3 flex items-center justify-center">
                        <svg
                          className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                          />
                        </svg>
                      </div>
                      <h3 className="font-bold text-slate-900 mb-1 text-sm sm:text-base">
                        AI Strategist
                      </h3>
                      <p className="text-xs sm:text-sm text-slate-600 font-semibold">
                        Systems-level AI implementation
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-4 sm:p-6 text-center">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-lg mx-auto mb-3 flex items-center justify-center">
                        <svg
                          className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                          />
                        </svg>
                      </div>
                      <h3 className="font-bold text-slate-900 mb-1 text-sm sm:text-base">
                        People-First
                      </h3>
                      <p className="text-xs sm:text-sm text-slate-600 font-semibold">
                        AI frees people to be people
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-4 sm:p-6 text-center">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-lg mx-auto mb-3 flex items-center justify-center">
                        <svg
                          className="w-5 h-5 sm:w-6 sm:h-6 text-green-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"
                          />
                        </svg>
                      </div>
                      <h3 className="font-bold text-slate-900 mb-1 text-sm sm:text-base">
                        Industry Speaker
                      </h3>
                      <p className="text-xs sm:text-sm text-slate-600 font-semibold">
                        AI implementation &amp; readiness
                      </p>
                    </div>
                  </div>
                </div>

                {/* RIGHT: Founder Photo + Stats */}
                <div className="order-2 md:!order-none max-w-sm mx-auto md:max-w-md md:ml-auto">
                  <div className="relative">
                    <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                      <img
                        src="/noah1-desaturated.jpeg"
                        alt="Noah Riley - Founder & CEO"
                        className="w-full h-auto object-cover"
                        loading="lazy"
                      />
                      {/* Photo overlay gradient */}
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent" />
                    </div>
                    {/* Decorative element */}
                    <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#B87333] rounded-2xl -z-10 opacity-20" />
                    <div className="absolute -top-6 -left-6 w-32 h-32 bg-blue-600 rounded-2xl -z-10 opacity-20" />
                  </div>

                  {/* Stats beneath photo */}
                  <div className="flex gap-4 mt-8">
                    <div className="bg-blue-50 px-6 py-3 rounded-xl flex-1 text-center">
                      <p className="text-3xl font-bold text-blue-600">10+</p>
                      <p className="text-sm text-slate-600 font-semibold">
                        Years Experience
                      </p>
                    </div>
                    <div className="bg-purple-50 px-6 py-3 rounded-xl flex-1 text-center">
                      <p className="text-3xl font-bold text-purple-600">6σ</p>
                      <p className="text-sm text-slate-600 font-semibold">
                        Six Sigma Certified
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* MISSION & VISION SECTION */}
        <div className="max-w-[1600px] mx-auto px-0 sm:px-12">
          <section className="relative bg-white py-16 sm:py-32 overflow-hidden shadow-2xl rounded-none sm:rounded-3xl">
            <div className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-6">
              <div className="grid md:grid-cols-2 gap-12">
                {/* MISSION */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 sm:p-10">
                  <div className="w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center mb-6">
                    <svg
                      className="w-8 h-8 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-3xl font-bold text-slate-900 mb-4">
                    Our Mission
                  </h3>
                  <p className="text-slate-700 font-semibold leading-relaxed text-lg">
                    To bring AI from "some powerful thing in the distance" into
                    the heart of your business—empowering your people and
                    processes with intelligent automation that delivers
                    measurable results. We don't just implement technology; we
                    build systems that make your entire operation run more
                    efficiently.
                  </p>
                </div>

                {/* VISION */}
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-6 sm:p-10">
                  <div className="w-16 h-16 bg-purple-600 rounded-xl flex items-center justify-center mb-6">
                    <svg
                      className="w-8 h-8 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-3xl font-bold text-slate-900 mb-4">
                    Our Vision
                  </h3>
                  <p className="text-slate-700 font-semibold leading-relaxed text-lg">
                    A future where every business—regardless of size—has access
                    to AI that works as a force multiplier for their team. Where
                    curiosity beats resistance, and automation frees people to
                    focus on what they do best: the creative, meaningful,
                    uniquely human work that no machine can replicate.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* CORE VALUES SECTION */}
        <div className="max-w-[1600px] mx-auto px-0 sm:px-12">
          <section className="relative bg-white py-16 sm:py-32 overflow-hidden shadow-2xl rounded-none sm:rounded-3xl">
            <div className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-6">
              <div className="text-center mb-16">
                <p className="text-sm uppercase tracking-widest text-slate-500 font-semibold mb-4">
                  Core Values
                </p>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 leading-tight mb-4 sm:mb-6">
                  What We Stand For
                </h2>
                <p className="text-xl text-slate-600 font-semibold max-w-[700px] mx-auto">
                  Our principles guide every decision, project, and client
                  relationship.
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {/* VALUE 1 */}
                <div className="bg-white rounded-2xl p-5 sm:p-8 border-2 border-slate-200">
                  <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                    <svg
                      className="w-7 h-7 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    Results-Driven
                  </h3>
                  <p className="text-slate-600 font-semibold leading-relaxed">
                    "AI is just a part of a larger system. It's a tool that we
                    put into a system to make it run more efficiently." We take
                    that literally—if it's not delivering measurable ROI, it's
                    not doing its job. Every solution we build is designed to
                    produce outcomes you can see in your bottom line.
                  </p>
                </div>

                {/* VALUE 2 */}
                <div className="bg-white rounded-2xl p-5 sm:p-8 border-2 border-slate-200">
                  <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
                    <svg
                      className="w-7 h-7 text-purple-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    Curiosity
                  </h3>
                  <p className="text-slate-600 font-semibold leading-relaxed">
                    "Curiosity beats resistance every time." We approach every
                    challenge by asking "How can I use this technology?" rather
                    than why. This mindset lets us find solutions others
                    overlook and turn emerging technology into practical
                    advantages.
                  </p>
                </div>

                {/* VALUE 3 */}
                <div className="bg-white rounded-2xl p-5 sm:p-8 border-2 border-slate-200">
                  <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-6">
                    <svg
                      className="w-7 h-7 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    Partnership
                  </h3>
                  <p className="text-slate-600 font-semibold leading-relaxed">
                    "It's a force multiplier. It becomes the exponent of
                    you"—but "if you equal zero, it doesn't matter what it
                    multiplies to." We invest deeply in understanding your
                    business, your people, and your processes, because the best
                    technology is worthless without the right human foundation.
                  </p>
                </div>

                {/* VALUE 4 */}
                <div className="bg-white rounded-2xl p-5 sm:p-8 border-2 border-slate-200">
                  <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-6">
                    <svg
                      className="w-7 h-7 text-orange-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    Human-First
                  </h3>
                  <p className="text-slate-600 font-semibold leading-relaxed">
                    "Why not let it do robot stuff so we can get back to doing
                    human stuff, the things that we love." We build AI and
                    software that gives people their time back—so they can focus
                    on the work that actually matters to them.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* CTA SECTION */}
        <div className="max-w-[1600px] mx-auto px-0 sm:px-12">
          <section className="relative bg-gradient-to-br from-slate-900 to-slate-800 py-16 sm:py-20 overflow-hidden shadow-2xl rounded-none sm:rounded-3xl">
            <div className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-6 text-center">
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-6">
                Ready to Transform Your Business?
              </h2>
              <p className="text-xl text-slate-300 font-semibold mb-10 max-w-[700px] mx-auto">
                Let's discuss how we can help you break through operational
                bottlenecks and achieve sustainable growth.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center px-8 py-4 bg-[#B87333] text-white font-semibold rounded-xl hover:bg-[#A66329] transition-colors shadow-lg"
                >
                  Get in Touch
                  <svg
                    className="w-5 h-5 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </a>
                <a
                  href="https://calendar.notion.so/meet/aigeniusconsulting/793ez04oz7"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-slate-900 font-semibold rounded-xl hover:bg-slate-100 transition-colors shadow-lg"
                >
                  Book a Strategy Call
                  <svg
                    className="w-5 h-5 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </a>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default AboutPage;
